import React, {useEffect, useMemo, useRef, useState} from 'react';
import {PhoneSectionBar} from '../Profiles/Components/PhoneSections';
import useMediaQuery from '@mui/material/useMediaQuery';
import onNextFrame from '../../../utility/onNextFrame';
import {Link, Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import {useGetEmployeeByCurrentUserQuery, useGetEmployeeByIdQuery} from '../../../api/services/employeeService';
import {useGetUserPermissionsQuery} from '../../../api/services/accountService';
import {ROLES} from '../../../constants/roles';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useUser from '../../../utility/hooks/useUser';
import {getStaffSectionAccess, STAFF_PERMISSION_MODULES, STAFF_SECTION_ACCESS} from './staffProfileSectionAccess';
import {STAFF_TAB_SECTIONS} from './EmployeeProfile';
import {compactProfileTheme, PROFILE_TABS_SX, tabsPerRow} from '../Profiles/StudentProfileDetails';
import {ThemeProvider} from '@mui/material/styles';
import {toTestId} from '../../../utility/testIdUtil';
import {OUTLINED_BOX_SX} from '../Profiles/ProfileTabs/FormsTab/tabOutline';

// Maria, 19 October 2026: the open tab is a lighter blue, and its page and the line round both are the same blue
const FRAME_BLUE = '#e4eefa';
// the border is the colour of the other tabs' borders
const FRAME_BORDER = '#c5d3e6';
const FRAME_LINE = `1px solid ${FRAME_BORDER}`;

// Maria, 18 October 2026: the staff profile in tabs, laid out like the student profile: card tabs under the menu,
// the open tab light blue, and 'David Abbot - Contacts' on one line under them.
const allTabs = [
    {label: 'Profile', path: '', title: 'Staff Profile', sections: STAFF_TAB_SECTIONS.profile},
    {label: 'Contacts', path: 'contacts', title: 'Contacts and Next of Kin', sections: STAFF_TAB_SECTIONS.contacts},
    // Maria, 19 October 2026: 'Banking' (it was 'Bank and NI' for a while)
    {label: 'Banking', path: 'banking', title: 'Bank and NI Details', sections: STAFF_TAB_SECTIONS.banking},
    {label: 'Cars', path: 'cars', title: 'Cars', sections: STAFF_TAB_SECTIONS.cars},
    // Maria, 19 October 2026: Documents before Forms, so the Forms sub-tabs can run across the whole page
    {label: 'Documents', path: 'documentation', title: 'Documents'},
    {label: 'Forms', path: 'forms', title: 'Forms'},
    // Maria, 19 October 2026: a Groups tab, as on the student profile, listing the groups of staff they are in
    {label: 'Groups', path: 'groups', title: 'Groups', adminOnly: true},
];

// Maria, 18 October 2026: the staff tabs fill the width like the student tabs, two rows of three on a phone, and are not
// as tall, as there is no status line under their names
// Maria, 19 October 2026: the student profile's tabs, with the name of the page under them, and no frame round the page
const STAFF_TABS_SX = {
    ...PROFILE_TABS_SX,
    ...tabsPerRow([[1199, 7], [760, 4], [480, 3]]),
    '& .MuiTab-root': {...PROFILE_TABS_SX['& .MuiTab-root'], minHeight: 40, justifyContent: 'center'}
};

const pathOf = pathname => {
    const match = allTabs.find(tab => tab.path && new RegExp(`/${tab.path}(/|$)`).test(pathname));
    return match ? match.path : '';
};

// AD-2738: an account made from an invitation is linked to its staff profile at the next SIMS staff sync, by the email
// on the SIMS staff record. Until then My Profile shows only this message, without tabs or blank fields.
// Maria, 20 October 2026: the wording
export const NOT_LINKED_MESSAGE = 'Your Admicity account has not yet been linked to your staff record in SIMS. ' +
    'Your profile will be shown here after the next SIMS synchronisation. ' +
    'The email address on your SIMS staff record must match the email address you use to log in to Admicity.';

// Maria, 19 October 2026: My Profile shows the same staff profile, the member of staff's own record, with the
// Employee column of Manage Access. Their forms are on the My Forms page, so My Profile has no Forms tab.
export const EmployeeProfileDetails = ({self}) => {
    const {id} = useParams();
    const byId = useGetEmployeeByIdQuery(id, {skip: self});
    const own = useGetEmployeeByCurrentUserQuery(undefined, {skip: !self});
    const {data = {}, isLoading} = self ? own : byId;
    // the backend answers 404 while the account has no staff profile
    const notLinked = self && own.error?.status === 404;
    const {data: employeePermissions = [], isSuccess: employeePermissionsLoaded} =
        useGetUserPermissionsQuery(ROLES.EMPLOYEE, {skip: !self});
    const {user} = useUser();
    const navigate = useNavigate();
    const isPhone = useMediaQuery('(max-width:599.95px)');
    const {pathname} = useLocation();
    const activePath = pathOf(pathname);
    const permissions = self ? employeePermissions : (user.permissions || []);
    // a tab of sections shows when the person may see at least one of its sections. On My Profile the tabs wait
    // for the Employee permissions, and no permissions there means every section is hidden.
    const canSee = tab => {
        if (self && (tab.path === 'forms' || tab.adminOnly)) return false;
        if (!tab.sections) return true;
        if (self && !employeePermissionsLoaded) return false;
        if (!self && permissions.length === 0) return true;
        return tab.sections.some(section => getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES[section]) !== STAFF_SECTION_ACCESS.hide);
    };
    const tabs = useMemo(() => allTabs.filter(canSee), [permissions, self, employeePermissionsLoaded]);
    const activeTab = tabs.find(tab => tab.path === activePath) ?? tabs[0];
    // Maria, 19 October 2026: the name is in each tab, so the pages do not repeat it
    const staffName = [data.legalForename, data.legalSurname].filter(Boolean).join(' ');
    // the tabs each framed page starts and ends at. Maria, 19 October 2026: Forms and Documents run across all the tabs
    const frameSpan = {
        '': ['profile', 'cars'],
        contacts: ['contacts', 'cars'],
        banking: ['banking', 'cars'],
        cars: ['cars', 'documentation'],
        // Maria, 19 October 2026: the Forms area ends at the Forms tab, now the last tab, in line with Approvals
        forms: ['profile', 'forms'],
        // Maria, 19 October 2026: Documents runs from its own tab to the end of the page
        documentation: ['documentation', self ? 'documentation' : 'forms']
    }[activePath];
    // Maria, 19 October 2026: the page is no longer framed, so it uses the full width as the student profile does
    const frameEnd = null && frameSpan?.[1];
    // Forms and Documents hold their own white box, so the frame round them is narrow
    const wideFrame = activePath === 'forms' || activePath === 'documentation';
    const pageRef = useRef(null);
    const [frame, setFrame] = useState(null);

    useEffect(() => {
        if (!frameEnd) {
            setFrame(null);
            return undefined;
        }
        const measure = () => {
            const page = pageRef.current?.getBoundingClientRect();
            const open = document.querySelector(`[data-tab-path="${activePath || 'profile'}"]`)?.getBoundingClientRect();
            const start = document.querySelector(`[data-tab-path="${frameSpan[0]}"]`)?.getBoundingClientRect();
            const end = document.querySelector(`[data-tab-path="${frameEnd}"]`)?.getBoundingClientRect();
            // only while the tabs are on one row; on a narrow screen the page uses the full width
            setFrame(page && open && start && end && Math.abs(start.bottom - end.bottom) < 2
                ? {
                    left: Math.round(start.left - page.left),
                    width: Math.round(end.right - start.left),
                    openStart: Math.round(open.left - start.left),
                    openEnd: Math.round(open.right - start.left)
                }
                : null);
        };
        const frameId = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        // the tabs can change width without the window changing, for example when a scroll bar appears
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onNextFrame(() => measure()));
        document.querySelectorAll('[data-tab-path]').forEach(tab => observer?.observe(tab));
        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [activePath, frameEnd, tabs.length, Object.keys(data).length]);

    useEffect(() => {
        if (self && !employeePermissionsLoaded) return;
        if (!tabs.some(tab => tab.path === activePath) && tabs[0]) {
            navigate(tabs[0].path || '.', {replace: true});
        }
    }, [tabs, activePath, navigate, self, employeePermissionsLoaded]);

    const pageSx = frame
        ? {
            fontSize: '0.875rem',
            lineHeight: 1.25,
            ...OUTLINED_BOX_SX,
            border: FRAME_LINE,
            borderTop: 'none',
            mt: 0,
            ml: `${frame.left}px`,
            width: `${frame.width}px`,
            // Maria, 19 October 2026: on Forms the white area runs to the edges of the frame, so the first sub-tab starts
            // where the Profile tab starts and Approvals ends where the Forms tab ends
            px: activePath === 'forms' ? 0 : 1,
            pt: wideFrame ? 1.25 : 1.5,
            pb: wideFrame ? 0.5 : 0,
            backgroundColor: FRAME_BLUE,
            '& [data-staff-forms-area]': {border: 'none', borderRadius: 0}
        }
        : {fontSize: '0.875rem', lineHeight: 1.25, mt: 0};

    if (notLinked) {
        return (
            <Box sx={{pt: 2}}>
                <Alert severity="info" data-testid="my-profile-not-linked">{NOT_LINKED_MESSAGE}</Alert>
            </Box>
        );
    }

    return (
        <Stack flexGrow={1} ref={pageRef}>
            <Stack
                direction="row"
                alignItems="flex-end"
                sx={{mt: 0, borderBottom: frame ? 'none' : '2px solid #d6e6f8', position: 'sticky', top: 0, zIndex: 5, backgroundColor: '#F0F4F8', pt: 0.5}}
            >
                {/* Maria, 19 October 2026: on a phone, one bar with the open section and arrows, as on a student's
                    profile, so every section has room, Groups included */}
                {isPhone && tabs.length > 0 && (
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <PhoneSectionBar
                            tabs={tabs}
                            index={Math.max(0, tabs.findIndex(tab => tab.path === activeTab?.path))}
                            statusOf={() => null}
                            onSelect={index => navigate(tabs[index].path || '.')}
                        />
                    </Box>
                )}
                {!isPhone && <Tabs
                    value={activeTab?.path ?? false}
                    textColor="inherit"
                    TabIndicatorProps={{sx: {display: 'none'}}}
                    sx={frame
                        ? {...STAFF_TABS_SX, '& .MuiTab-root.Mui-selected': {...STAFF_TABS_SX['& .MuiTab-root.Mui-selected'], border: FRAME_LINE, borderBottom: 'none', backgroundColor: FRAME_BLUE, position: 'relative', zIndex: 2}}
                        : STAFF_TABS_SX}
                >
                    {tabs.map(tab => (
                        <Tab
                            key={tab.path}
                            value={tab.path}
                            label={tab.label}
                            component={Link}
                            to={tab.path}
                            data-testid={toTestId('employee-profile-tab', tab.label)}
                            data-tab-path={tab.path || 'profile'}
                            disableRipple
                        />
                    ))}
                </Tabs>}
            </Stack>
            {staffName && (
                <Typography variant="h5" component="h1" fontWeight={600} sx={{mt: 1, mb: 1}} data-testid="staff-profile-heading">
                    {activeTab?.path ? `${staffName} - ${activeTab.title}` : staffName}
                </Typography>
            )}
            {/* Maria, 19 October 2026: on Contacts, Bank and NI and Cars the page starts level with the name */}
            <Box sx={{position: 'relative'}}>
            {/* Maria, 18 October 2026: the same smaller boxes and text as the student profile, in edit mode too */}
            <ThemeProvider theme={compactProfileTheme}>
                <Box sx={pageSx}>
                    {/* the top of the page's box, under the tabs it reaches across */}
                    {frame && frame.openStart > 0 && <Box sx={{position: 'absolute', top: 0, left: 0, width: `${frame.openStart}px`, height: '1px', backgroundColor: FRAME_BORDER}}/>}
                    {frame && <Box sx={{position: 'absolute', top: 0, left: `${frame.openEnd}px`, right: 0, height: '1px', backgroundColor: FRAME_BORDER}}/>}
                    <Outlet context={{data, isLoading, framed: Boolean(frame), staffPermissions: self ? employeePermissions : undefined, self}}/>
                </Box>
            </ThemeProvider>
            </Box>
        </Stack>
    );
};

EmployeeProfileDetails.propTypes = {
    self: PropTypes.bool
};

export default EmployeeProfileDetails;
